import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  fromEvaluation,
  formatOfferAmount,
  offerBenefitRows,
  type CheckoutPromotionViewData,
  type OfferBenefitKind,
  type OfferBenefitView,
} from '../../features/offers/offerBenefits'
import {
  selectIsOfferLoading,
  selectOfferEvaluation,
} from '../../features/offers/offersSlice'
import type { PromotionFreeItem } from '../../features/offers/promotionEvaluation'
import CurrencyDisplay from '../ui/CurrencyDisplay'
import { useTranslation } from '../../hooks/useTranslation'

/** Figma tokens — node 16163:288 (Mobile / QR full card) */
const OFFER_BG = '#EDF9F2'
const OFFER_GREEN = '#1A5C35'
const OFFER_GREEN_SOFT = '#C8EDD8'
const OFFER_PRODUCT_BG = '#C8EDD8'
const OFFER_DIVIDER = '#1A5C35'
const OFFER_TITLE = '#1E1E1E'
const OFFER_DESC = '#6B7280'

const ICON_DISCOUNT = '/offers/offer_discount.svg'
const ICON_CASHBACK = '/offers/offer_cashback.svg'
const ICON_FREE = '/offers/offer_free_products.svg'

function iconForKind(kind: OfferBenefitKind): string {
  if (kind === 'cashback') return ICON_CASHBACK
  if (kind === 'free') return ICON_FREE
  return ICON_DISCOUNT
}

function resolveLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  title?: string,
  titleKey?: string,
): string {
  if (title && title.trim().length > 0) return title
  if (titleKey) return t(titleKey)
  return ''
}

function resolveDescription(
  t: (key: string, options?: Record<string, unknown>) => string,
  benefit: OfferBenefitView,
): string | null {
  if (benefit.description && benefit.description.trim().length > 0) {
    return benefit.description
  }
  if (benefit.descriptionKey) {
    return t(benefit.descriptionKey, benefit.descriptionParams)
  }
  return null
}

function figmaTitle(
  t: (key: string) => string,
  benefit: OfferBenefitView,
): string {
  if (benefit.kind === 'set_item') return t('offers.benefitSetItemSavings')
  if (benefit.showMinus || benefit.kind === 'discount') return t('offers.benefitDiscount')
  if (benefit.kind === 'cashback') return t('offers.benefitCashback')
  return resolveLabel(t, benefit.title, benefit.titleKey) || t('offers.benefitDiscount')
}

function figmaDescription(
  t: (key: string, options?: Record<string, unknown>) => string,
  benefit: OfferBenefitView,
): string | null {
  const direct = resolveDescription(t, benefit)
  if (direct) return direct

  const fallbackTitles = new Set([
    t('offers.gotDiscount'),
    t('offers.gotCashback'),
    t('offers.gotGift'),
    t('offers.benefitDiscount'),
    t('offers.benefitCashback'),
    t('offers.benefitFreeProducts'),
  ])

  const title = resolveLabel(t, benefit.title, benefit.titleKey)
  const heading = figmaTitle(t, benefit)
  if (title && title !== heading && !fallbackTitles.has(title)) {
    return title
  }

  if (benefit.kind === 'cashback' && benefit.amount != null && benefit.amount > 0) {
    return t('offers.cashbackCredited', {
      amount: formatOfferAmount(benefit.amount),
    })
  }

  return null
}

const OfferBenefitIcon: React.FC<{ kind: OfferBenefitKind; size?: number }> = ({
  kind,
  size = 24,
}) => (
  <img
    src={iconForKind(kind)}
    alt=""
    width={size}
    height={size}
    className="shrink-0"
    style={{ width: size, height: size }}
  />
)

const ValueBadge: React.FC<{ amount: number; showMinus: boolean }> = ({
  amount,
  showMinus,
}) => (
  <div
    className="flex items-center gap-1 px-2 py-1 rounded-lg shrink-0"
    style={{ backgroundColor: OFFER_GREEN }}
  >
    <CurrencyDisplay
      className="w-3 h-3"
      iconStyle={{ filter: 'brightness(0) invert(1)' }}
    />
    <span className="text-xs font-bold text-white font-['IBM_Plex_Sans_Arabic']">
      {showMinus ? '-' : ''}
      {formatOfferAmount(amount)}
    </span>
  </div>
)

const FreeProductCard: React.FC<{ item: PromotionFreeItem }> = ({ item }) => {
  const { t } = useTranslation()
  const title =
    item.name && item.name.length > 0
      ? item.quantity != null && item.quantity > 1
        ? `${item.name} ${item.quantity}x`
        : item.name
      : t('offers.gotGift')

  return (
    <div
      className="flex items-center gap-2 px-2 py-2 rounded-xl min-w-[168px] max-w-[200px]"
      style={{ backgroundColor: OFFER_PRODUCT_BG }}
    >
      <OfferBenefitIcon kind="free" size={18} />
      {item.image ? (
        <img
          src={item.image}
          alt=""
          className="w-9 h-9 rounded-lg object-cover shrink-0"
          onError={(e) => {
            ;(e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div
          className="text-xs font-semibold leading-tight line-clamp-2 font-['IBM_Plex_Sans_Arabic']"
          style={{ color: OFFER_TITLE }}
        >
          {title}
        </div>
        {item.originalPrice != null && item.originalPrice > 0 ? (
          <div
            className="flex items-center gap-0.5 mt-1 text-[11px] line-through font-['IBM_Plex_Sans_Arabic']"
            style={{ color: OFFER_DESC }}
          >
            <span>{formatOfferAmount(item.originalPrice)}</span>
            <CurrencyDisplay className="w-2.5 h-2.5" />
          </div>
        ) : null}
      </div>
    </div>
  )
}

const BenefitRow: React.FC<{ benefit: OfferBenefitView }> = ({ benefit }) => {
  const { t } = useTranslation()
  const title = figmaTitle(t, benefit)
  const description = figmaDescription(t, benefit)

  return (
    <div className="flex items-start gap-2.5">
      <OfferBenefitIcon kind={benefit.kind} size={24} />
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-bold leading-tight font-['IBM_Plex_Sans_Arabic']"
          style={{ color: OFFER_TITLE }}
        >
          {title}
        </div>
        {description ? (
          <div
            className="text-xs mt-1 leading-snug font-['IBM_Plex_Sans_Arabic']"
            style={{ color: OFFER_DESC }}
          >
            {description}
          </div>
        ) : null}
      </div>
      {benefit.amount != null && benefit.amount > 0 ? (
        <ValueBadge amount={benefit.amount} showMinus={benefit.showMinus} />
      ) : null}
    </div>
  )
}

export interface CheckoutOfferCardProps {
  /** When set, renders order promotion display-only (no Redux evaluate). */
  display?: CheckoutPromotionViewData
}

/**
 * Full Offer Card matching Besalla-Mobile / Figma 16163:288 (always expanded).
 */
const CheckoutOfferCard: React.FC<CheckoutOfferCardProps> = ({ display: displayProp }) => {
  const { t, isRTL } = useTranslation()
  const evaluation = useSelector(selectOfferEvaluation)
  const isLoading = useSelector(selectIsOfferLoading)

  const benefits = useMemo(() => {
    if (displayProp) return offerBenefitRows(displayProp)
    if (!evaluation.hasEligibleOffer) return []
    return offerBenefitRows(fromEvaluation(evaluation))
  }, [displayProp, evaluation])

  if (!displayProp && isLoading) {
    return (
      <div className="w-full py-3 flex" dir={isRTL ? 'rtl' : 'ltr'}>
        <div
          className="w-5 h-5 border-2 rounded-full animate-spin"
          style={{ borderColor: OFFER_GREEN, borderTopColor: 'transparent' }}
          aria-label="loading"
        />
      </div>
    )
  }

  if (benefits.length === 0) return null

  const valueRows = benefits.filter((row) => row.products.length === 0)
  const products = benefits.flatMap((row) => row.products)
  const countLabel =
    benefits.length === 1
      ? t('offers.benefitCount', { count: benefits.length })
      : t('offers.benefitsCount', { count: benefits.length })

  return (
    <div
      className="w-full font-['IBM_Plex_Sans_Arabic']"
      style={{
        backgroundColor: OFFER_BG,
        border: `1.5px dashed ${OFFER_GREEN}`,
        borderRadius: 12,
        padding: 16,
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none shrink-0" aria-hidden>
          🎉
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold leading-tight" style={{ color: OFFER_GREEN }}>
            {t('offers.savingMore')}
          </div>
        </div>
        <div
          className="px-2.5 py-1.5 rounded-full text-xs font-semibold shrink-0"
          style={{ backgroundColor: OFFER_GREEN_SOFT, color: OFFER_GREEN }}
        >
          {countLabel}
        </div>
      </div>

      <div
        className="w-full my-4 border-t border-dashed"
        style={{ borderColor: OFFER_DIVIDER }}
      />

      <div className="flex flex-col gap-4">
        {valueRows.map((benefit, index) => (
          <BenefitRow key={`benefit-${index}`} benefit={benefit} />
        ))}
        {products.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            <div className="text-sm font-bold" style={{ color: OFFER_TITLE }}>
              {t('offers.benefitFreeProducts')}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {products.map((item, index) => (
                <FreeProductCard
                  key={`${item.productId ?? item.id ?? item.name}-${index}`}
                  item={item}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default CheckoutOfferCard
